import { useEffect, useMemo, useState } from "react";
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Link,
    Typography
} from "@mui/material";

export type TermsAndConditionsMethod = "login" | "signup";

export function TermsAndConditions(props: {
    method: TermsAndConditionsMethod;
    acceptTerms?: boolean;
    onAcceptTermsChange?: (accepted: boolean) => void;
    showAcceptTermsError?: boolean;
}) {
    const { method, acceptTerms, onAcceptTermsChange, showAcceptTermsError } = props;
    const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState<string>("");
    const [termsOfServiceUrl, setTermsOfServiceUrl] = useState<string>("");

    useEffect(() => {
        const run = async () => {
            try {
                const res = await fetch("/public/config", {
                    credentials: "include"
                });
                if (!res.ok) {
                    return;
                }
                const body = await res.json().catch(() => ({}));
                setPrivacyPolicyUrl(
                    body && typeof body.privacyPolicyUrl === "string"
                        ? body.privacyPolicyUrl
                        : ""
                );
                setTermsOfServiceUrl(
                    body && typeof body.termsOfServiceUrl === "string"
                        ? body.termsOfServiceUrl
                        : ""
                );
            } catch {
                // optional
            }
        };
        run();
    }, []);

    const prefixText = useMemo(() => {
        return method === "login"
            ? "Durch deine Anmeldung stimmst du unseren "
            : "Ich akzeptiere die ";
    }, [method]);

    const legalLinks = (
        <>
            {termsOfServiceUrl ? (
                <Link
                    href={termsOfServiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                >
                    Nutzungsbedingungen
                </Link>
            ) : (
                "Nutzungsbedingungen"
            )}{" "}
            {method === "login" ? "zu und nimmst unsere " : "und die "}{" "}
            {privacyPolicyUrl ? (
                <Link
                    href={privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                >
                    Datenschutzerklärung
                </Link>
            ) : (
                "Datenschutzerklärung"
            )}{" "}
            {method === "login" ? "zur Kenntnis." : "zur Kenntnis."}
        </>
    );

    if (method === "signup" && onAcceptTermsChange) {
        const shouldShowPersistentHint = !acceptTerms;
        return (
            <FormControl
                error={!!showAcceptTermsError}
                sx={{ width: "100%", alignItems: "flex-start" }}
            >
                <FormControlLabel
                    sx={{
                        alignItems: "flex-start",
                        ml: 0,
                        mr: 0,
                        ".MuiFormControlLabel-label": { pt: 0.25 }
                    }}
                    control={
                        <Checkbox
                            checked={!!acceptTerms}
                            onChange={(event) =>
                                onAcceptTermsChange(event.target.checked)
                            }
                            sx={{ mt: -0.25 }}
                        />
                    }
                    label={
                        <Typography
                            variant="body2"
                            sx={{ opacity: 0.9, lineHeight: 1.45 }}
                        >
                            {prefixText}
                            {legalLinks}
                        </Typography>
                    }
                />
                {shouldShowPersistentHint ? (
                    <FormHelperText
                        error={!!showAcceptTermsError}
                        sx={{
                            mt: 0.25,
                            ml: 0,
                            pl: "40px",
                            lineHeight: 1.35,
                            opacity: showAcceptTermsError ? 1 : 0.85
                        }}
                    >
                        Bitte akzeptiere die Nutzungsbedingungen und
                        Datenschutzbestimmungen, um fortzufahren.
                    </FormHelperText>
                ) : null}
            </FormControl>
        );
    }

    return (
        <Typography variant="body2" sx={{ opacity: 0.8, textAlign: "center" }}>
            {prefixText}
            {legalLinks}
        </Typography>
    );
}

